import {
  formatCategorySearchRequestOptions,
  formatIdentity,
  formatIdentityItem,
  formatPaginatedResponseItems,
  formatRequestOptions,
  formatSearchRequestOptions,
  returnPaginatedResponse,
} from "./format";
import type { IResponseEvent } from "./events/event.types";
import Event from "./events/event.model";
import useRequest from "~/hooks/use-request";
import HeaderAPI from "~/services/header";
import { EVENT_TYPE } from "~/constants";

const SearchAPI = {
  eventSearch(options: IModelSearchOptions): Promise<IPaginatedResponse<IModelEvent>> {
    const { request } = useRequest();
    const params = formatSearchRequestOptions(options);
    const searchType = options.type === EVENT_TYPE.HIGHLIGHT ? "vod" : "event";

    return request
      .post<IPaginatedResponse<IResponseEvent>>(`/api/${searchType}/search`, { body: params })
      .then(Event.formatPaginated);
  },
  async categoryWithSubcategorySearch(options: IModelCategorySearchOptions): Promise<IModelCategorySearch[]> {
    const { request } = useRequest();
    const appConfig = useRuntimeConfig().public;
    const cachedCategory = useSessionStorage<IHeaderLink[]>(`${appConfig.APP_CUSTOMER.name_short}-links`, []);

    if (!(cachedCategory.value && cachedCategory.value.length))
      await HeaderAPI.load(); // Check if category is already exists on sessios storage

    const params = formatCategorySearchRequestOptions(options);

    return request
      .post<IPaginatedResponse<IResponseCategorySearch>>("/api/identity/search", { body: params })
      .then((response) => {
        const categoryResults = response.content.entries.reduce((acc: IModelCategorySearch[], cur) => {
          const mainIdentityId = cur.mainIdentityId;
          const title = cachedCategory.value.find(c => c.id === mainIdentityId)?.title;
          if (!title)
            return acc;
          const item = { mainIdentityId, title, identityItems: cur.identityItems.map(formatIdentityItem) };
          return [...acc, item];
        }, []);

        return categoryResults;
      });
  },
  categoryWithSubcategorySearchById(options: IModelCategorySearchOptionsWithIdentity): Promise<any> {
    return SearchAPI.categoryWithSubcategorySearch(options).then((response) => {
      if (response.length === 0)
        return Promise.resolve(returnPaginatedResponse([]));

      return returnPaginatedResponse(response[0].identityItems);
    });
  },

  categorySearch(options: IRequestPaginatedOptions & { searchString?: string }): Promise<IPaginatedResponse<IModelIdentity>> {
    const { request } = useRequest();
    const generalParams = formatRequestOptions(options);
    const params = { ...generalParams, type: "identity", archived: options?.archived, searchString: options?.searchString };

    return request
      .post<IPaginatedResponse<IResponseIdentity>>("/api/identity/category_search", { body: params })
      .then(response => formatPaginatedResponseItems(response, formatIdentity));
  },
};

export default SearchAPI;
